import React, { useCallback, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useFieldArray, useWatch, useFormState, useFormContext } from 'react-hook-form';
import classNames from 'classnames';
import { isEqual } from 'lodash';
import { downloadCsvFile, toCSV } from 'common/utils/helpers';
import { useGetActionsQuery } from 'apis/cm';
import { useGetCountersQuery } from 'apis/pm';
import { AddIcon, ExportIcon } from 'components/icons';
import Button from 'components/shared/Button';
import IconButton from 'components/shared/IconButton';
import { ProgressIndicator } from 'components/shared/Progress';
import { adjustTimeRange, findBestResolution } from 'components/shared/Chart';
import {
  DeviceChart,
  DeviceChartEditableTitle,
  DeviceChartError,
  getDefaultTitle
} from 'components/shared/DeviceChart';
import { getTimeRange } from 'components/shared/TimeSelect';
import { KPIFieldset } from './Form';
import { getNextCounters, getNextScales, createDataset, getNextAvailableCounter } from './utils';
import styles from './DeviceChartConfig.module.css';
import { getCounterSuffix } from '../../utils';
import { isGa2Device } from 'common/utils/deviceTwinUtils';

function DeviceChartConfig({
  availableCounters,
  onCancel,
  onSubmit,
  width,
  height,
  createPreference,
  local = false,
  placeholder = false
}) {
  const { handleSubmit, setValue, getValues } = useFormContext();
  const { isValid, dirtyFields, isSubmitting } = useFormState();
  const activeTabDeviceId = useSelector((state) => state.ui.pivotOverviewPage.activeTab);
  const donor = useSelector((state) => state.ui.singleDevicePage.selectedDevice);
  const devices = useWatch({ name: 'state.query.devices' });
  const counters = useWatch({ name: 'state.query.counters' });
  const actions = useWatch({ name: 'state.query.actions' });
  const time = useWatch({ name: 'state.query.time' });
  const currDatasets = useWatch({ name: 'state.datasets' });
  const { fields: datasets, remove, append } = useFieldArray({ name: 'state.datasets' });
  const defaultName = useMemo(
    () => (placeholder ? 'Provide a chart name' : getDefaultTitle(counters ?? [])),
    [counters, placeholder]
  );
  const timeRange = useMemo(() => getTimeRange(time), [time]);
  const resolution = useMemo(() => {
    const [from, to] = timeRange;
    const duration = new Date(to).getTime() - new Date(from).getTime();

    return findBestResolution(800, duration);
  }, [timeRange]);
  const [from, to] = useMemo(
    () => (resolution ? adjustTimeRange(timeRange, resolution) : timeRange),
    [timeRange, resolution]
  );
  const deviceCountersQuery = useGetCountersQuery(
    {
      devices: isGa2Device(donor) ? [donor.id] : devices,
      counters,
      from,
      to,
      interval: resolution,
      suffix: getCounterSuffix(donor, activeTabDeviceId)
    },
    {
      placeholderData: [],
      retry: (failureCount, error) => error?.response?.status !== 500 && failureCount < 3
    }
  );
  const deviceActionsQuery = useGetActionsQuery(
    {
      devices,
      actions,
      runtimeStatuses: ['Completed'],
      from,
      to
    },
    {
      enabled: actions?.length > 0,
      placeholderData: []
    }
  );
  const handleKeyDown = useCallback((event) => {
    // prevent submit on enter
    if (event.keyCode === 13) {
      event.preventDefault();
    }
  }, []);
  const handleExportData = useCallback(() => {
    const csv = toCSV(
      [
        {
          name: 'timestamp',
          accessor: (row) => row.timestamp && new Date(row.timestamp).toISOString()
        },
        ...counters
      ],
      deviceCountersQuery.data
    );

    downloadCsvFile(csv, 'chartExportData.csv');
  }, [counters, deviceCountersQuery.data]);
  const updateCounters = useCallback(() => {
    const currDatasets = getValues('state.datasets');
    const prevCounters = getValues('state.query.counters');
    const nextCounters = getNextCounters(currDatasets, prevCounters);

    if (nextCounters !== prevCounters) {
      setValue('state.query.counters', nextCounters);
    }
  }, [getValues, setValue]);
  const updateScales = useCallback(() => {
    const currDatasets = getValues('state.datasets');
    const prevScales = getValues('state.scales');
    const nextScales = getNextScales(currDatasets, prevScales);

    if (!isEqual(prevScales, nextScales)) {
      setValue('state.scales', nextScales);
    }
  }, [getValues, setValue]);
  const handleAddDataset = useCallback(
    (append) => () => {
      const nextAvailableCounter = getNextAvailableCounter(availableCounters, getValues('state.query.counters'));
      append(createDataset(nextAvailableCounter));
    },
    [getValues, availableCounters]
  );

  useEffect(() => {
    updateCounters();
    updateScales();
  }, [updateCounters, updateScales, datasets, currDatasets]);

  const disableSaveAsNew =
    deviceCountersQuery.isFetching ||
    deviceCountersQuery.error != null ||
    !isValid ||
    !dirtyFields.name ||
    isSubmitting;

  const disableSave =
    deviceCountersQuery.isFetching || deviceCountersQuery.error != null || !isValid || local || isSubmitting;

  return (
    <form
      id="chartConfigModalOptionsForm"
      onSubmit={handleSubmit(onSubmit)}
      onKeyDown={handleKeyDown}
      className={styles.form}
    >
      <div className={styles.header}>
        <DeviceChartEditableTitle variant="config" editable={true} defaultValue={defaultName} />
        <IconButton
          aria-label="Export"
          data-tip="Export data"
          variant="text"
          color="secondary"
          size="x-small"
          className={styles.exportButton}
          disabled={deviceCountersQuery.isFetching}
          onClick={handleExportData}
        >
          <ExportIcon size="medium" />
        </IconButton>
      </div>
      <div className={styles.body} style={{ width: `${width}px`, height: `${height}px` }}>
        <div className={styles.container}>
          <div className={styles.tabs}>
            <div className={styles.tabList}>
              <div className={classNames(styles.tabItem, styles.tabItemActive)}>
                <div className={styles.tabItemIndicator} />
                <div className={styles.tabItemLabel}>Time chart view</div>
              </div>
            </div>
          </div>
          <div className={styles.tabPanel}>
            <div className={styles.config}>
              {datasets.map((dataset, index) => (
                <KPIFieldset
                  key={dataset.id}
                  name={`state.datasets.${index}`}
                  value={dataset}
                  index={index}
                  options={availableCounters}
                  onSelect={createDataset}
                  onKeyChange={updateCounters}
                  onAxisChange={updateScales}
                  onRemove={remove}
                  removable={datasets.length > 1}
                />
              ))}
              {datasets.length < 3 && (
                <Button
                  variant="text"
                  color="primary"
                  size="medium"
                  start={<AddIcon size="small" />}
                  className={styles.addButton}
                  onClick={handleAddDataset(append)}
                >
                  Add another KPI
                </Button>
              )}
            </div>
            <div className={styles.chart}>
              {deviceCountersQuery.isFetching && <ProgressIndicator className={styles.progress} />}
              {deviceCountersQuery.error != null ? (
                <DeviceChartError retrying={deviceCountersQuery.isFetching} />
              ) : (
                <DeviceChart.Preview
                  counters={deviceCountersQuery.data}
                  actions={deviceActionsQuery.data}
                  actionsEnabled={actions?.length > 0}
                  height={295}
                />
              )}
            </div>
          </div>
        </div>
        <div className={styles.actions}>
          <Button variant="link" color="primary" size="medium" className={styles.cancelButton} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            id="chartConfigModalOptionsFormSubmit"
            variant="mixed"
            color="primary"
            size="medium"
            className={styles.saveAsButton}
            data-tip={
              disableSaveAsNew
                ? 'Saving as a new chart requires chart name to be modified. Please modify chart name.'
                : undefined
            }
            disabled={disableSaveAsNew}
            onClick={handleSubmit(createPreference)}
          >
            Save As New
          </Button>
          <Button
            id="chartConfigModalOptionsFormSubmit"
            type="submit"
            variant="contained"
            color="primary"
            size="medium"
            className={styles.submitButton}
            data-tip={
              local && !dirtyFields.name
                ? 'Initial set of charts cannot be modified. Please choose new name and select Save As New.'
                : undefined
            }
            disabled={disableSave}
          >
            Save
          </Button>
        </div>
      </div>
    </form>
  );
}

export default DeviceChartConfig;
